import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import numbro from 'numbro';
import { shallow } from 'zustand/shallow';

import {
  ReportSummaryPopup,
  SummaryType,
} from '../../components/Popups/ReportSummaryPopup';
import {
  PeriodList,
  SelectPeriodPopup,
} from '../../components/Popups/SelectPeriodPopup';
import { PerformanceReportTemplate } from '../../components/Templates';

import { usePopup } from '../../context';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import { SpaceListType } from '../../models/SpaceStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useUserStore } from '../../stores/UserStore';
import {
  useRecordStore,
  useRecordStoreMapField,
} from '../../stores/RecordStore';
import { arrayEqual } from '../../utils/array';
import { getUiLocale } from '../../utils/i18n';
import i18n, { TranslationLocale } from '../../i18n';

type State = {
  isLoading: boolean;
  period: string;
  selectedPeriod: string;
  managedClassroomId: string | undefined | null;
};

const { fetchSpaceSummaries } = useRecordStore.getState();

const fetchManagedSpaces: FetchListFn = (cursor, signal) =>
  useSpaceStore.getState().fetchManagedSpaces(cursor, { signal });

export const PerformanceReportScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showPopup, hidePopup } = usePopup();

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const activeRegion = useSessionStore((store) => store.activeRegion);

  const [state, setState] = useState<State>({
    isLoading: true,
    period: '',
    selectedPeriod: 'Overall', // todo need to update the list base on selected period later, now always overall
    managedClassroomId: undefined,
  });

  const isSubAccount = false;

  const userAccount = useUserStore((store) =>
    store.getAccount(activeUserId ?? ''),
  );
  const userProfile = useUserStore((store) =>
    store.getProfile(activeUserId ?? ''),
  );

  useEffect(() => {
    if (!activeUserId || !userProfile) {
      navigate('/start');
    }
  }, [activeUserId, navigate, userProfile]);

  useEffect(() => {
    if (
      userProfile?.worksForJoyajoy ||
      (userAccount?.managedClassroomExpiredAt &&
        userAccount?.managedClassroomExpiredAt > new Date())
    ) {
      // do nothing
    } else {
      navigate('/smart-parents');
    }
  }, [
    navigate,
    userAccount?.managedClassroomExpiredAt,
    userProfile?.worksForJoyajoy,
  ]);

  const managedSpaceField = useSpaceStoreListField(
    'spaces',
    SpaceListType.Managed,
  );
  const {
    isFinished: isFinishedManagedSpace,
    fetchAll: fetchAllManagedSpaces,
  } = useFetchList(managedSpaceField, fetchManagedSpaces);

  useEffect(() => {
    fetchAllManagedSpaces().catch(console.error);
  }, [fetchAllManagedSpaces]);

  const managedCollection = useSpaceStore((store) => {
    return store.getSpaces(SpaceListType.Managed);
  }, arrayEqual(shallow));

  useEffect(() => {
    const managedClassroom = managedCollection[0];
    if (managedClassroom) {
      console.log('success get id', managedClassroom.classroomId);
      setState((s) => ({
        ...s,
        managedClassroomId: managedClassroom.classroomId,
      }));
    } else {
      if (isFinishedManagedSpace) {
        setState((s) => ({ ...s, managedClassroomId: null }));
      }
    }
  }, [isFinishedManagedSpace, managedCollection]);

  const formatDate = useCallback(
    (timestamp: number): string => {
      if (timestamp === 0) {
        timestamp = new Date().getTime();
      }
      const date = new Date(timestamp);
      return date
        .toLocaleDateString(getUiLocale(i18n.language as TranslationLocale), {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
        .replace(',', '');
    },
    [t],
  );

  useRecordStoreMapField(
    'spaceSummaries',
    `classroom_${state.managedClassroomId}`,
  );

  useEffect(() => {
    if (state.managedClassroomId) {
      fetchSpaceSummaries(
        { classroomIds: [state.managedClassroomId] },
        { headers: { 'cache-control': 'no-cache' } },
      )
        .then(() => {
          setState((s) => ({ ...s, isLoading: false }));
        })
        .catch(console.error);
    }
  }, [state.managedClassroomId]);

  const spaceSummary = useRecordStore(
    (store) => store.getSpaceSummary(state.managedClassroomId ?? ''),
    shallow,
  );

  // useEffect(() => {
  //   if (spaceSummary) {
  //     setState((s) => ({ ...s, isLoading: false }));
  //   } else if (spaceSummary === null) {
  //     console.log(''); // error message
  //     setState((s) => ({ ...s, isLoading: false }));
  //   }
  // }, [spaceSummary]);

  useEffect(() => {
    if (spaceSummary) {
      setState((s) => ({ ...s, isLoading: false }));
    } else if (spaceSummary === null) {
      console.log(''); // error message
      setState((s) => ({ ...s, isLoading: false }));
    }
  }, [spaceSummary]);

  const initialDates = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date(spaceSummary?.createdAt || 0);
    const dates = {
      startDate,
      endDate,
      formattedStartDate: formatDate(startDate.getTime()),
      formattedEndDate: formatDate(endDate.getTime()),
    };
    setState((s) => ({
      ...s,
      period: `${dates.formattedStartDate} - ${dates.formattedEndDate}`,
    }));
  }, [formatDate, spaceSummary?.createdAt]);

  const today = useMemo(() => {
    return new Date();
  }, []);

  // const formattedDate = today.toLocaleDateString(activeRegion, {
  //   day: 'numeric',
  //   month: 'short',
  //   year: 'numeric',
  // });

  const formatTime = (timestamp: number): string => {
    if (timestamp === 0) {
      return '';
    }
    const date = new Date(timestamp);
    return date.toLocaleTimeString(activeRegion, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const msToTime = (ms: number): string => {
    const seconds = Math.floor((ms / 1000) % 60);
    const minutes = Math.floor((ms / (1000 * 60)) % 60);
    const hours = Math.floor(ms / (1000 * 60 * 60));

    const parts: string[] = [];

    if (hours > 0) {
      parts.push(`${hours}hr`);
    }
    if (minutes > 0) {
      parts.push(`${minutes}m`);
    }
    if (seconds > 0 || parts.length === 0) {
      parts.push(`${seconds}s`);
    }

    return parts.join(' ');
  };

  const makePeriodList = useMemo(() => {
    const currentDate = new Date();
    const createdAt = new Date(
      spaceSummary?.createdAt || currentDate.getTime(),
    );

    const getDateRange = (start: Date, end: Date) => {
      if (!spaceSummary?.createdAt || end < createdAt) return '';
      return start < createdAt
        ? `${formatDate(createdAt.getTime())} - ${formatDate(end.getTime())}`
        : `${formatDate(start.getTime())} - ${formatDate(end.getTime())}`;
    };

    const formatSingleDate = (date: Date) =>
      date
        .toLocaleDateString(activeRegion, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
        .replace(',', '');

    const formatMonthYear = (date: Date) =>
      date.toLocaleDateString(activeRegion, {
        month: 'short',
        year: 'numeric',
      });

    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - currentDate.getDay());

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    const lastWeekStart = new Date(weekStart);
    lastWeekStart.setDate(weekStart.getDate() - 7);

    const lastWeekEnd = new Date(weekEnd);
    lastWeekEnd.setDate(weekEnd.getDate() - 7);

    const thisMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1,
    );
    const lastMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
      1,
    );
    const lastMonthEnd = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      0,
    );

    return [
      {
        name: 'Overall',
        range: `${state.period}`,
        selected: state.selectedPeriod === 'Overall',
      },
      {
        name: 'Today',
        range: formatSingleDate(currentDate),
        selected: state.selectedPeriod === 'Today',
      },
      {
        name: 'This Week',
        range: getDateRange(weekStart, weekEnd),
        selected: state.selectedPeriod === 'This Week',
      },
      {
        name: 'Last Week',
        range: getDateRange(lastWeekStart, lastWeekEnd),
        selected: state.selectedPeriod === 'Last Week',
      },
      {
        name: 'This Month',
        range: thisMonth < createdAt ? '' : formatMonthYear(thisMonth),
        selected: state.selectedPeriod === 'This Month',
      },
      {
        name: 'Last Month',
        range: lastMonthEnd < createdAt ? '' : formatMonthYear(lastMonth),
        selected: state.selectedPeriod === 'Last Month',
      },
    ] as PeriodList;
  }, [
    activeRegion,
    formatDate,
    state.period,
    state.selectedPeriod,
    spaceSummary?.createdAt,
  ]);

  // Handler Methods
  const handlePeriodClick = useCallback(() => {
    showPopup(
      <SelectPeriodPopup
        onPrimary={hidePopup}
        onPeriod={(selected, range) => {
          hidePopup();
          setState((s) => ({ ...s, selectedPeriod: selected, period: range }));
        }}
        PeriodList={makePeriodList}
      />,
    );
  }, [hidePopup, makePeriodList, showPopup]);

  const navigateGameList = useCallback(() => {
    navigate('/club/journey');
    hidePopup();
  }, [hidePopup, navigate]);

  const handleGamesPlayedClick = useCallback(() => {
    showPopup(
      <ReportSummaryPopup
        onPrimaryButtonClick={hidePopup}
        summaryType={SummaryType.GamePlayed}
        value={String(spaceSummary?.completedGameCount || 0)}
        isSubAccount={isSubAccount}
        onPressPlay={navigateGameList}
      />,
    );
  }, [
    hidePopup,
    isSubAccount,
    navigateGameList,
    showPopup,
    spaceSummary?.completedGameCount,
  ]);

  const handleGameScoreClick = useCallback(() => {
    showPopup(
      <ReportSummaryPopup
        onPrimaryButtonClick={hidePopup}
        summaryType={SummaryType.GameScore}
        value={numbro(spaceSummary?.averageGameScore ?? 0).format({
          mantissa: 2,
          trimMantissa: true,
          optionalMantissa: true,
        })}
        isSubAccount={isSubAccount}
        onPressPlay={navigateGameList}
      />,
    );
  }, [
    hidePopup,
    isSubAccount,
    navigateGameList,
    showPopup,
    spaceSummary?.averageGameScore,
  ]);

  const handleTimeSpentClick = useCallback(() => {
    showPopup(
      <ReportSummaryPopup
        onPrimaryButtonClick={hidePopup}
        summaryType={SummaryType.TimePlayed}
        value={String(msToTime(spaceSummary?.totalDurationInMs || 0))}
        isSubAccount={isSubAccount}
        onPressPlay={navigateGameList}
      />,
    );
  }, [
    hidePopup,
    isSubAccount,
    navigateGameList,
    showPopup,
    spaceSummary?.totalDurationInMs,
  ]);

  // Performance Level Click Handlers
  const handleExcellentClick = useCallback(() => {
    // Navigate to excellent performance details
  }, []);

  const handleVeryGoodClick = useCallback(() => {
    // Navigate to very good performance details
  }, []);

  const handleGoodClick = useCallback(() => {
    // Navigate to good performance details
  }, []);

  const handleSatisfactoryClick = useCallback(() => {
    // Navigate to satisfactory performance details
  }, []);

  const handleFairClick = useCallback(() => {
    // Navigate to fair performance details
  }, []);

  const handleBeginnerClick = useCallback(() => {
    // Navigate to beginner performance details
  }, []);

  const templateProps = {
    lastModified: {
      date:
        spaceSummary === null
          ? t('No report yet')
          : formatDate(spaceSummary?.lastModifiedAt.valueOf() || 0),
      time:
        spaceSummary === null
          ? ''
          : formatTime(spaceSummary?.lastModifiedAt.valueOf() || 0),
    },
    selectedPeriod: state.selectedPeriod,
    period: {
      range: state.period,
      onClick: handlePeriodClick,
    },
    summary: {
      memoryRetention:
        spaceSummary?.forgettingCurve?.memoryRetentionValues[60] || 0,
      gamesPlayed: {
        count: spaceSummary?.completedGameCount || 0,
        onClick: handleGamesPlayedClick,
      },
      gameScore: {
        average: parseFloat(
          numbro(spaceSummary?.averageGameScore ?? 0).format({
            mantissa: 2,
            trimMantissa: true,
            optionalMantissa: true,
          }),
        ),
        onClick: handleGameScoreClick,
      },
      timeSpent: {
        duration: msToTime(spaceSummary?.totalDurationInMs || 0),
        onClick: handleTimeSpentClick,
      },
    },
    analysisReports: {
      excellent: {
        count: spaceSummary?.performances[5] || 0,
        onClick: handleExcellentClick,
      },
      veryGood: {
        count: spaceSummary?.performances[4] || 0,
        onClick: handleVeryGoodClick,
      },
      good: {
        count: spaceSummary?.performances[3] || 0,
        onClick: handleGoodClick,
      },
      satisfactory: {
        count: spaceSummary?.performances[2] || 0,
        onClick: handleSatisfactoryClick,
      },
      fair: {
        count: spaceSummary?.performances[1] || 0,
        onClick: handleFairClick,
      },
      beginner: {
        count: spaceSummary?.performances[0] || 0,
        onClick: handleBeginnerClick,
      },
    },
    isLoading: state.isLoading,
  };

  return <PerformanceReportTemplate {...templateProps} />;
};

export default PerformanceReportScreen;
